import { useMemo, useState } from "react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Loader2, Search, ZoomIn } from "lucide-react";

import { Input } from "@/shared/ui/input";
import { Button } from "@/shared/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/ui/card";
import { Badge } from "@/shared/ui/badge";
import {
  TableCell,
  TableRow,
} from "@/shared/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/dialog";
import { Pagination, DataTable, MonthPicker } from "@/shared/widgets";

import {
  useReceivableSnapshotSummaries,
  useReceivableSnapshotDetail,
} from "../hooks";
import type {
  ReceivableSnapshotSummary,
} from "../utils/types";
import { useTranslation } from '@/shared/i18n';

const DEFAULT_PAGE_SIZE = 10;

const currencyFormatter = (currency?: string | null) =>
  new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: currency ?? "BRL",
    minimumFractionDigits: 2,
  });

const percentFormatter = new Intl.NumberFormat("pt-BR", {
  style: "percent",
  maximumFractionDigits: 3,
  minimumFractionDigits: 2,
});

const LIKEAPPRO_PERCENTAGE = 0.6;
const CREATORS_PERCENTAGE = 0.4;

const splitFinalAmount = (amount: number | undefined | null) => {
  const total = amount ?? 0;
  return {
    likeappro: total * LIKEAPPRO_PERCENTAGE,
    creators: total * CREATORS_PERCENTAGE,
  };
};

const formatDateTime = (value?: string | null) => {
  if (!value) return "—";
  try {
    return format(new Date(value), "dd/MM/yyyy HH:mm", { locale: ptBR });
  } catch {
    return value;
  }
};

const formatReferenceMonth = (value?: string | null) => {
  if (!value) return "—";
  const [year, month] = value.split("-");
  if (!year || !month) return value;
  // Cria data no timezone local, não UTC
  const date = new Date(Number(year), Number(month) - 1, 1);
  if (Number.isNaN(date.getTime())) return value;
  return format(date, "MMM/yyyy", { locale: ptBR });
};

// Função auxiliar para converter valores para número válido, tratando null, undefined e NaN
const toNumber = (value: number | null | undefined): number => {
  if (value == null || Number.isNaN(value)) return 0;
  return Number(value) || 0;
};

export function ReceivableSnapshotsList() {
  const { t } = useTranslation('receivable-snapshots');
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(DEFAULT_PAGE_SIZE);
  const [selectedSummary, setSelectedSummary] = useState<ReceivableSnapshotSummary | null>(null);
  const [paidDateMonth, setPaidDateMonth] = useState<{ month: number; year: number } | null>(null);
  const [referenceDateMonth, setReferenceDateMonth] = useState<{ month: number; year: number } | null>(null);

  // Formata o mês para o formato MM-YYYY da API
  const formatMonthForApi = (monthValue: { month: number; year: number } | null): string | undefined => {
    if (!monthValue) return undefined;
    const month = String(monthValue.month).padStart(2, "0");
    return `${month}-${monthValue.year}`;
  };

  const { summaries, totalPages, totalItems, isLoading, isFetching, error } =
    useReceivableSnapshotSummaries({
      page,
      limit,
      search: search.trim() || undefined,
      paidDate: formatMonthForApi(paidDateMonth),
      reference_date: formatMonthForApi(referenceDateMonth),
    });

  const paidDate = selectedSummary?.paymentMonth?.split("-").reverse().join("-") ?? "";

  const {
    snapshots: creatorSnapshots,
    isLoading: loadingDetail,
  } = useReceivableSnapshotDetail(paidDate, {
    
    enabled: Boolean(paidDate),
  });

  const closeDetail = () => setSelectedSummary(null);

  const metricsFormatter = useMemo(() => {
    const currency = selectedSummary?.currency || "BRL";
    return currencyFormatter(currency);
  }, [selectedSummary?.currency]);

  const creatorTotals = useMemo(
    () =>
      creatorSnapshots.reduce(
        (acc, snapshot) => ({
          final: acc.final + toNumber(snapshot.shareAmount),
          share: acc.share + toNumber(snapshot.sharePercentage),
          traffic: acc.traffic + toNumber(snapshot.trafficValue) / 100,
          averageTraffic: acc.averageTraffic + toNumber(snapshot.averageTrafficValue) / 100,
          preset: acc.preset + toNumber(snapshot.useValue) / 100,
        }),
        { final: 0, share: 0, traffic: 0, averageTraffic: 0, preset: 0 },
      ),
    [creatorSnapshots],
  );

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
    setPage(1);
  };

  // Reset page when filters change
  const handlePaidDateChange = (value: { month: number; year: number } | null) => {
    setPaidDateMonth(value);
    setPage(1);
  };

  const handleReferenceDateChange = (value: { month: number; year: number } | null) => {
    setReferenceDateMonth(value);
    setPage(1);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t('receivableCalculations')}</h1>
          <p className="text-muted-foreground">
            {t('trackCalculatedSnapshots')}
          </p>
        </div>
      </div>

      {/* Filtros */}
      <Card className="bg-white/[0.03] border-white/[0.06]">
        <CardHeader>
          <CardTitle>{t('filters')}</CardTitle>
          <CardDescription>{t('searchByReceivableId')}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-4 md:flex-row md:items-center">
            <div className="relative md:w-80">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder={t('searchPlaceholder')}
                value={search}
                onChange={handleSearchChange}
                className="pl-9"
              />
            </div>
            <div className="w-full md:w-[280px]">
              <MonthPicker
                value={paidDateMonth}
                onChange={handlePaidDateChange}
                placeholder={t('receiptMonth')}
              />
            </div>
            <div className="w-full md:w-[280px]">
              <MonthPicker
                value={referenceDateMonth}
                onChange={handleReferenceDateChange}
                placeholder={t('referenceMonth')}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabela */}
      <DataTable
        data={summaries}
        isLoading={isLoading}
        error={error}
        emptyTitle={t('emptyTitle')}
        emptyDescription={(hasFilters) =>
          hasFilters
            ? t('emptyDescriptionWithFilters')
            : t('emptyDescription')
        }
        hasFilters={Boolean(search || paidDateMonth || referenceDateMonth)}
        errorMessage={`${t('errorLoadingCalculations')}: ${error?.message}`}
        rowKey={(summary) => summary.receivableId}
        columns={[
          {
            header: t('receiptMonth'),
            cell: (summary) => summary.paymentMonth.split("-").reverse().join("/"),
          },
          {
            header: t('status'),
            cell: (summary) => (
                          <Badge variant={summary.status === "CONCLUDED" ? "default" : "secondary"}>
                            {summary.status ?? "—"}
                          </Badge>
            ),
          },
          {
            header: t('creators'),
            cell: (summary) => summary.creatorCount,
          },
          {
            header: t('gross'),
            cell: (summary) => {
              const formatter = currencyFormatter(summary.currency);
              return formatter.format(summary.grossAmount);
            },
          },
          {
            header: t('net'),
            cell: (summary) => {
              const formatter = currencyFormatter(summary.currency);
              return formatter.format(summary.netAmount);
            },
          },
          {
            header: t('creatorsValue'),
            cell: (summary) => {
              const formatter = currencyFormatter(summary.currency);
              return formatter.format(summary.shareAmount);
            },
          },
          {
            header: t('calculatedAt'),
            cell: (summary) => formatDateTime(summary.calculatedAt),
          },
          {
            header: t('details'),
            cell: (summary) => (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => setSelectedSummary(summary)}
                          >
                            <ZoomIn className="mr-2 h-4 w-4" />
                            {t('open')}
                          </Button>
            ),
            className: "text-right",
          },
        ]}
      />

      {/* Pagination */}
      {!isLoading && !error && totalPages > 0 && (
          <Pagination
            currentPage={page}
            totalPages={Math.max(totalPages, 1)}
            totalItems={totalItems}
          setCurrentPage={setPage}
            pageSize={limit}
          showPageSizeOptions
          pageSizeOptions={[10, 20, 50]}
            onPageSizeChange={(value) => {
              setLimit(value);
              setPage(1);
            }}
          showTotalItems
          syncWithQueryParams={false}
          />
      )}

      <Dialog open={Boolean(selectedSummary)} onOpenChange={(open) => !open && closeDetail()}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto space-y-5">
          <DialogHeader>
            <DialogTitle>{t('receivableDetails')}</DialogTitle>
            <DialogDescription>
              {t('viewCalculatedValues')}
            </DialogDescription>
          </DialogHeader>

          {selectedSummary && (
            <div className="space-y-4">
              <div className="grid gap-3 rounded-lg border border-white/10 bg-muted/10 p-4">
                <div>
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">{t('receiptMonth')}</p>
                  <p className="text-sm font-semibold">{selectedSummary.paymentMonth}</p>
                </div>
              </div>

              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                <MetricCard label={t('grossValue')} value={metricsFormatter.format(selectedSummary.grossAmount)} />
                <MetricCard label={t('netValue')} value={metricsFormatter.format(selectedSummary.netAmount)} />
                <MetricCard
                  label={t('creatorsValue')}
                  value={metricsFormatter.format(selectedSummary.shareAmount)}
                />
              </div>
            </div>
          )}

          <div className="rounded-lg border border-white/10">
            <div className="flex flex-col gap-2 border-b border-white/10 px-4 py-3 md:flex-row md:items-center md:justify-between">
              <div>
                <p className="text-sm font-semibold">{t('distributionBetweenCreators')}</p>
                <p className="text-xs text-muted-foreground">
                  {t('distributionDescription')}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {t('totalCreatorsLabel')}
                </span>
                <span className="rounded-md bg-primary/10 px-3 py-1 text-sm font-semibold text-primary">
                  {metricsFormatter.format(creatorTotals.final)}
                </span>
                {loadingDetail && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
              </div>
            </div>
            <div className="overflow-x-auto">
              <DataTable
                data={creatorSnapshots}
                isLoading={loadingDetail}
                emptyTitle={t('noSnapshotFound')}
                emptyDescription={() => t('noSnapshotFound')}
                noCard
                columns={[
                  {
                    header: t('creator'),
                    cell: (snapshot) => (
                      <div className="flex flex-col gap-0.5 font-mono text-xs">
                            <span className="font-semibold text-foreground">
                              {snapshot.creatorName || snapshot.creatorId}
                            </span>
                            <span className="text-muted-foreground">{snapshot.creatorId}</span>
                          </div>
                    ),
                    className: "font-mono text-xs",
                  },
                  {
                    header: t('sharePercentage'),
                    cell: (snapshot) => percentFormatter.format(toNumber(snapshot.sharePercentage) / 100),
                  },
                  {
                    header: t('totalCreators'),
                    cell: (snapshot) => metricsFormatter.format(toNumber(snapshot.shareAmount)),
                  },
                  {
                    header: t('trafficPercentage'),
                    cell: (snapshot) => percentFormatter.format(toNumber(snapshot.trafficValue)),
                  },
                  {
                    header: t('averageTrafficClicks'),
                    cell: (snapshot) => percentFormatter.format(toNumber(snapshot.averageTrafficValue)),
                  },
                  {
                    header: t('presetUsage'),
                    cell: (snapshot) => percentFormatter.format(toNumber(snapshot.useValue) / 100),
                  },
                ]}
                footer={
                  <TableRow className="bg-muted/20 font-semibold">
                    <TableCell>{t('totals')}</TableCell>
                    <TableCell>{percentFormatter.format(toNumber(creatorTotals.share) / 100)}</TableCell>
                    <TableCell>{metricsFormatter.format(toNumber(creatorTotals.final))}</TableCell>
                    <TableCell>{percentFormatter.format(toNumber(creatorTotals.traffic))}</TableCell>
                    <TableCell>{percentFormatter.format(toNumber(creatorTotals.averageTraffic))}</TableCell>
                    <TableCell>{percentFormatter.format(toNumber(creatorTotals.preset))}</TableCell>
                  </TableRow>
                }
              />
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MetricCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-white/10 bg-muted/10 p-4">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="text-lg font-semibold">{value}</p>
    </div>
  );
}
